import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const rowsEs = [
  { topic: "Disponibilidad", before: "Solo en horario de oficina. Fuera de horario, llamadas perdidas.", after: "24 / 7 / 365. Cada llamada atendida al primer tono, siempre." },
  { topic: "Coste operativo", before: "Recepcionista a jornada completa: +25 000 € / año en salario + cotizaciones.", after: "Coste fijo desde 97 € / mes. Sin sorpresas, sin costes variables." },
  { topic: "Errores en reservas", before: "Dobles reservas, confusiones de horario y olvidos frecuentes.", after: "ISO 8601 + verificación en tiempo real. Cero conflictos, confirmación SMS automática." },
  { topic: "Tiempo de respuesta", before: "Esperas de 2–5 minutos o buzón de voz. El cliente cuelga.", after: "Respuesta instantánea < 1 s. El cliente habla con la IA de inmediato." },
  { topic: "Analíticas", before: "Sin datos. No sabes cuántas llamadas se pierden ni cuándo.", after: "Dashboard en tiempo real: volumen, duración, outcome y tendencias mensuales." },
  { topic: "Escalabilidad", before: "Más clientes = más personal. Escalar es lento y caro.", after: "Volumen ilimitado sin coste adicional. Crece sin fricciones." },
];

const rowsEn = [
  { topic: "Availability", before: "Office hours only. Outside hours, missed calls.", after: "24 / 7 / 365. Every call answered on the first ring, always." },
  { topic: "Operating cost", before: "Full-time receptionist: +€25,000/year in salary + social charges.", after: "Fixed cost from €97/mo. No surprises, no variable costs." },
  { topic: "Booking errors", before: "Double bookings, schedule confusion and frequent oversights.", after: "ISO 8601 + real-time verification. Zero conflicts, automatic SMS confirmation." },
  { topic: "Response time", before: "2–5 minute waits or voicemail. The client hangs up.", after: "Instant response < 1 s. The client speaks to the AI immediately." },
  { topic: "Analytics", before: "No data. You don't know how many calls are missed or when.", after: "Real-time dashboard: volume, duration, outcome and monthly trends." },
  { topic: "Scalability", before: "More clients = more staff. Scaling is slow and expensive.", after: "Unlimited volume at no extra cost. Grow without friction." },
];

const metricsEs = [
  { value: "96%", label: "Llamadas resueltas sin intervención humana" },
  { value: "< 1 s", label: "Tiempo de respuesta medio" },
  { value: "−74%", label: "Coste operativo vs recepcionista tradicional" },
];

const metricsEn = [
  { value: "96%", label: "Calls resolved without human intervention" },
  { value: "< 1 s", label: "Average response time" },
  { value: "−74%", label: "Operating cost vs traditional receptionist" },
];

export default function ComparisonSection() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === "en";
  const rows = isEn ? rowsEn : rowsEs;
  const metrics = isEn ? metricsEn : metricsEs;
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-28 bg-stone-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-4">
            {t("comp_label")}
          </span>
          <h2
            className="text-4xl lg:text-5xl font-bold text-stone-900 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("comp_title_1")}
            <br />
            <span className="text-amber-500">{t("comp_title_2")}</span>
          </h2>
          <p className="mt-4 text-stone-500 text-lg max-w-lg mx-auto">
            {t("comp_subtitle")}
          </p>
        </div>

        {/* Metrics banner */}
        <div className={`grid grid-cols-3 gap-4 mb-12 transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {metrics.map((m) => (
            <div key={m.label} className="text-center bg-white border border-stone-100 rounded-2xl py-6 px-4">
              <p className="text-3xl font-black text-amber-500 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                {m.value}
              </p>
              <p className="text-stone-500 text-xs leading-snug">{m.label}</p>
            </div>
          ))}
        </div>

        {/* Comparison table */}
        <div className={`transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {/* Column headers */}
          <div className="grid grid-cols-[180px_1fr_1fr] gap-4 mb-3 px-1">
            <div />
            <div className="flex items-center gap-2 px-5 py-3 bg-stone-100 rounded-xl">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-stone-300 flex-shrink-0">
                <i className="ri-close-line text-stone-500 text-sm" />
              </div>
              <span className="font-bold text-stone-500 text-sm">{t("comp_without")}</span>
            </div>
            <div className="flex items-center gap-2 px-5 py-3 bg-amber-500 rounded-xl">
              <div className="w-6 h-6 flex items-center justify-center rounded-full bg-white/30 flex-shrink-0">
                <i className="ri-check-line text-white text-sm" />
              </div>
              <span className="font-bold text-white text-sm">{t("comp_with")}</span>
            </div>
          </div>

          {/* Rows */}
          <div className="space-y-2">
            {rows.map((row, i) => (
              <div
                key={row.topic}
                className={`grid grid-cols-[180px_1fr_1fr] gap-4 items-stretch transition-all duration-500 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"}`}
                style={{ transitionDelay: `${250 + i * 70}ms` }}
              >
                <div className="flex items-center px-4">
                  <span className="text-stone-600 text-sm font-semibold">{row.topic}</span>
                </div>
                <div className="flex items-start gap-3 bg-white border border-stone-100 rounded-2xl px-5 py-4">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-red-50 border border-red-100 mt-0.5 flex-shrink-0">
                    <i className="ri-close-line text-red-400 text-xs" />
                  </div>
                  <p className="text-stone-500 text-sm leading-relaxed">{row.before}</p>
                </div>
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-2xl px-5 py-4">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-400 mt-0.5 flex-shrink-0">
                    <i className="ri-check-line text-white text-xs" />
                  </div>
                  <p className="text-stone-700 text-sm font-medium leading-relaxed">{row.after}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className={`mt-12 text-center transition-all duration-700 delay-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <a
            href="/#precios"
            className="whitespace-nowrap inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-semibold text-sm px-8 py-3.5 rounded-full transition-colors cursor-pointer"
          >
            {t("comp_cta")}
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </div>
    </section>
  );
}
